/**
 * Sorteio e ajuste dos times.
 *
 * No desktop o ajuste é por arrastar e soltar — aqui faz sentido, ao contrário
 * do celular, onde o gesto compete com a rolagem. Há também o caminho por
 * toque (selecionar um jogador → tocar num jogador de outro time para trocar,
 * ou em "Mover para cá"), que funciona no celular, com teclado e com leitor de
 * tela; drag-and-drop sozinho seria inacessível.
 *
 * Trocar é o gesto principal: mantém o tamanho dos times, que é o que importa
 * para equilibrar. Mover sozinho deixa um time com um a mais.
 */

import type { PlayerPosition } from '@nossorachao/types';
import { AlertTriangle, ArrowDownToLine, ArrowLeftRight, Hand, Save, Shuffle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/layout';
import { ShareTeamsButtons } from '@/components/share-teams';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  PositionBadge,
  Skeleton,
  StarRating,
  cx,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useGame, useGenerateTeams, useSaveTeams } from '@/lib/queries';
import { teamsToWhatsappText } from '@/lib/teams-text';

interface DraftPlayer {
  key: string;
  memberId?: string;
  guestId?: string;
  name: string;
  /**
   * Null quando o servidor não liberou a nota. Na prática não acontece nesta
   * tela (só administrador chega aqui), mas o tipo carrega a possibilidade e
   * tratá-la evita um `NaN` silencioso na média se a regra mudar.
   */
  skill: number | null;
  position: PlayerPosition;
  isGoalkeeper: boolean;
  isGuest: boolean;
  locked: boolean;
}

interface DraftTeam {
  name: string;
  color: string;
  players: DraftPlayer[];
  /**
   * "Fora dos times": confirmados e convidados que não estão em nenhum time
   * (quem confirmou ou foi convidado depois do sorteio). Fica no mesmo array
   * para mover e trocar funcionarem igual, mas não é salvo nem entra na média.
   */
  bench?: boolean;
}

const BENCH_NAME = 'Fora dos times';

export function TeamsPage() {
  const { gameId = '' } = useParams();
  const navigate = useNavigate();

  const { data: game, isLoading } = useGame(gameId);
  const generate = useGenerateTeams(gameId);
  const save = useSaveTeams(gameId);

  const [draft, setDraft] = useState<DraftTeam[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Sincroniza com o servidor, exceto quando há edição pendente — sobrescrever
  // o ajuste do admin por um refetch seria péssimo.
  useEffect(() => {
    if (!game || dirty) return;

    if (game.teams.length === 0) {
      setDraft([]);
      return;
    }

    const teams: DraftTeam[] = game.teams.map((team) => ({
      name: team.name,
      color: team.color,
      players: team.players.map((player) => ({
        key: player.id,
        memberId: player.member?.id,
        guestId: player.guest?.id,
        name: player.name,
        skill: player.skillRating,
        position: player.position,
        isGoalkeeper: player.isGoalkeeper,
        isGuest: Boolean(player.guest),
        locked: player.locked,
      })),
    }));

    const inTeams = new Set(
      game.teams.flatMap((team) =>
        team.players.map((player) => player.member?.id ?? player.guest?.id),
      ),
    );

    const bench: DraftPlayer[] = [
      ...game.roster.confirmed
        .filter((player) => !inTeams.has(player.member.id))
        .map((player) => ({
          key: `member:${player.member.id}`,
          memberId: player.member.id,
          name: player.member.nickname ?? player.member.user.name,
          skill: player.member.skillRating,
          position: player.member.position,
          isGoalkeeper: false,
          isGuest: false,
          locked: false,
        })),
      ...game.roster.guests
        .filter((guest) => !inTeams.has(guest.id))
        .map((guest) => ({
          key: `guest:${guest.id}`,
          guestId: guest.id,
          name: guest.name,
          skill: guest.skillRating,
          position: guest.position,
          isGoalkeeper: false,
          isGuest: true,
          locked: false,
        })),
    ];

    setDraft([...teams, { name: BENCH_NAME, color: '#98A2B3', players: bench, bench: true }]);
  }, [game, dirty]);

  function movePlayer(playerKey: string, targetIndex: number) {
    setDraft((current) => {
      const from = current.findIndex((team) =>
        team.players.some((player) => player.key === playerKey),
      );
      if (from === -1 || from === targetIndex) return current;

      const player = current[from]!.players.find((entry) => entry.key === playerKey)!;

      return current.map((team, index) => {
        if (index === from) {
          return { ...team, players: team.players.filter((entry) => entry.key !== playerKey) };
        }
        if (index === targetIndex) {
          return { ...team, players: [...team.players, arriving(player, team)] };
        }
        return team;
      });
    });

    setDirty(true);
    setSelected(null);
  }

  /** Troca dois jogadores de times diferentes — os times mantêm o tamanho. */
  function swapPlayers(firstKey: string, secondKey: string) {
    setDraft((current) => {
      const locate = (key: string) => {
        for (const [teamIndex, team] of current.entries()) {
          const playerIndex = team.players.findIndex((player) => player.key === key);
          if (playerIndex !== -1) return { teamIndex, playerIndex };
        }
        return null;
      };

      const first = locate(firstKey);
      const second = locate(secondKey);
      if (!first || !second || first.teamIndex === second.teamIndex) return current;

      const firstPlayer = current[first.teamIndex]!.players[first.playerIndex]!;
      const secondPlayer = current[second.teamIndex]!.players[second.playerIndex]!;

      return current.map((team, index) => {
        if (index === first.teamIndex) {
          const rest = team.players.filter((player) => player.key !== firstKey);
          const players = [...team.players];
          players[first.playerIndex] = arriving(secondPlayer, { ...team, players: rest });
          return { ...team, players };
        }
        if (index === second.teamIndex) {
          const rest = team.players.filter((player) => player.key !== secondKey);
          const players = [...team.players];
          players[second.playerIndex] = arriving(firstPlayer, { ...team, players: rest });
          return { ...team, players };
        }
        return team;
      });
    });

    setDirty(true);
    setSelected(null);
  }

  /** Toque num jogador: seleciona, desmarca ou troca com o já selecionado. */
  function handlePlayerClick(playerKey: string, teamIndex: number) {
    if (!selected || selected === playerKey) {
      setSelected(selected === playerKey ? null : playerKey);
      return;
    }

    const selectedTeam = draft.findIndex((team) =>
      team.players.some((player) => player.key === selected),
    );

    if (selectedTeam === teamIndex) setSelected(playerKey);
    else swapPlayers(selected, playerKey);
  }

  function toggleGoalkeeper(playerKey: string) {
    setDraft((current) =>
      current.map((team) => {
        if (!team.players.some((player) => player.key === playerKey)) return team;

        // Um goleiro por time: marcar um desmarca o anterior.
        return {
          ...team,
          players: team.players.map((player) => ({
            ...player,
            isGoalkeeper: player.key === playerKey ? !player.isGoalkeeper : false,
          })),
        };
      }),
    );
    setDirty(true);
  }

  async function handleSave() {
    await save.mutateAsync({
      // "Fora dos times" não vai: quem ficou lá não entra na escalação.
      teams: draft
        .filter((team) => !team.bench)
        .map((team) => ({
          name: team.name,
          color: team.color,
          players: team.players.map((player) => ({
            ...(player.memberId ? { memberId: player.memberId } : { guestId: player.guestId }),
            isGoalkeeper: player.isGoalkeeper,
            locked: player.locked,
          })),
        })),
    });

    setDirty(false);
    navigate(`/partidas/${gameId}`);
  }

  if (isLoading || !game) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const average = (team: DraftTeam) => {
    const rated = team.players.filter((player) => player.skill !== null);
    return rated.length
      ? rated.reduce((sum, player) => sum + (player.skill ?? 0), 0) / rated.length
      : 0;
  };

  const teams = draft.filter((team) => !team.bench);
  const benchCount = draft.find((team) => team.bench)?.players.length ?? 0;

  const spread =
    teams.length > 1 ? Math.max(...teams.map(average)) - Math.min(...teams.map(average)) : 0;

  const selectedTeamIndex = selected
    ? draft.findIndex((team) => team.players.some((player) => player.key === selected))
    : -1;

  const shareText = teamsToWhatsappText({
    groupName: game.groupName,
    scheduledAt: game.scheduledAt,
    venueName: game.venueName,
    teams,
  });

  return (
    <>
      <PageHeader
        title="Times"
        subtitle={
          game.roster.guests.length > 0
            ? `${game.confirmedCount} confirmados (${game.roster.guests.length} convidado(s))`
            : `${game.confirmedCount} confirmados`
        }
        back={{ to: `/partidas/${gameId}`, label: 'Partida' }}
        action={
          teams.length > 0 ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                icon={Shuffle}
                loading={generate.isPending}
                onClick={() => {
                  setDirty(false);
                  generate.mutate({});
                }}
              >
                Refazer sorteio
              </Button>
              <Button
                icon={Save}
                disabled={!dirty}
                loading={save.isPending}
                onClick={handleSave}
              >
                Salvar
              </Button>
            </div>
          ) : null
        }
      />

      {teams.length === 0 ? (
        <Card>
          <EmptyState
            icon={Shuffle}
            title="Times ainda não sorteados"
            description="O sorteio equilibra por nível e garante um goleiro em cada time. Repetir a escalação da semana passada é penalizado."
            action={
              <Button
                icon={Shuffle}
                loading={generate.isPending}
                onClick={() => generate.mutate({})}
              >
                Sortear times
              </Button>
            }
          />

          {generate.error ? (
            <p className="mt-2 text-center text-[13px] text-bad">
              {generate.error instanceof ApiError
                ? generate.error.message
                : 'Não foi possível sortear.'}
            </p>
          ) : null}
        </Card>
      ) : (
        <div className="space-y-5">
          {/* Relatório de equilíbrio */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">Equilíbrio</p>
                <p className="text-[13px] text-ink-2">
                  Diferença entre as médias:{' '}
                  <span className="tabular font-semibold">
                    {spread.toFixed(2).replace('.', ',')}
                  </span>
                </p>
              </div>

              <Badge tone={spread <= 0.15 ? 'ok' : spread <= 0.4 ? 'warn' : 'bad'}>
                {spread <= 0.15 ? 'Equilibrado' : spread <= 0.4 ? 'Aceitável' : 'Desigual'}
              </Badge>
            </div>

            {benchCount > 0 ? (
              <p className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-[13px] text-warn">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                {benchCount} confirmado(s) fora dos times — coloque em algum time ou refaça o
                sorteio.
              </p>
            ) : null}

            {generate.data?.warnings.length ? (
              <ul className="mt-3 space-y-1.5 border-t border-line pt-3">
                {generate.data.warnings.map((warning) => (
                  <li key={warning} className="flex items-start gap-2 text-[13px] text-ink-2">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn" aria-hidden />
                    {warning}
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">Mandar no grupo</p>
                <p className="text-[13px] text-ink-2">
                  {dirty
                    ? 'Vai com os ajustes desta tela. Salve também, para o app mostrar a mesma escalação.'
                    : 'Nomes e goleiros, sem as notas.'}
                </p>
              </div>
              <ShareTeamsButtons text={shareText} />
            </div>
          </Card>

          {selected ? (
            <Card accent="rgb(21 112 239)">
              <p className="flex items-center gap-2 text-[13px] text-ink-2">
                <ArrowLeftRight className="h-4 w-4 text-info" aria-hidden />
                Toque num jogador de outro time para <strong>trocar</strong> os dois, ou em
                “Mover para cá” para só mudar de time. Toque de novo nele para cancelar.
              </p>
            </Card>
          ) : null}

          <div className="grid gap-5 md:grid-cols-2">
            {draft.map((team, teamIndex) =>
              // A área "fora dos times" só aparece quando tem alguém nela, ou
              // quando há um jogador selecionado (para poder tirá-lo do time).
              team.bench && team.players.length === 0 && !selected ? null : (
              <Card
                key={team.name}
                accent={team.bench ? 'rgb(220 104 3)' : team.color}
                className={cx(
                  dragging && 'ring-2 ring-brand/30',
                  team.bench && 'order-first md:col-span-2',
                )}
              >
                <div
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const key = event.dataTransfer.getData('text/plain');
                    if (key) movePlayer(key, teamIndex);
                    setDragging(null);
                  }}
                  className="min-h-[120px]"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 font-bold">
                      {team.bench ? (
                        <AlertTriangle className="h-4 w-4 text-warn" aria-hidden />
                      ) : (
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: team.color }}
                        />
                      )}
                      {team.name}
                      <span className="text-[13px] font-normal text-ink-3">
                        {team.players.length} jogadores
                      </span>
                    </span>

                    <span className="flex items-center gap-2">
                      {selected && selectedTeamIndex !== teamIndex ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={ArrowDownToLine}
                          onClick={() => movePlayer(selected, teamIndex)}
                        >
                          Mover para cá
                        </Button>
                      ) : null}
                      {!team.bench && !team.players.some((player) => player.isGoalkeeper) ? (
                        <Badge tone="warn">Sem goleiro</Badge>
                      ) : null}
                      {!team.bench ? (
                        <span className="tabular text-[13px] font-bold text-ink-2">
                          {average(team).toFixed(2).replace('.', ',')}
                        </span>
                      ) : null}
                    </span>
                  </div>

                  {team.bench ? (
                    <p className="mb-3 text-[13px] text-ink-2">
                      Confirmados e convidados que ainda não estão em nenhum time. Toque em um
                      deles e depois em “Mover para cá” num time, ou num jogador de lá para
                      trocar. Quem ficar aqui não entra na escalação salva.
                    </p>
                  ) : null}

                  <ul className="space-y-1">
                    {keepersFirst(team.players).map((player) => {
                      const active = selected === player.key;
                      // Com alguém selecionado em outro time, este jogador é
                      // alvo de troca — o destaque mostra onde tocar.
                      const swapTarget =
                        selected !== null &&
                        selectedTeamIndex !== -1 &&
                        selectedTeamIndex !== teamIndex;

                      return (
                        <li key={player.key}>
                          <div
                            draggable
                            onDragStart={(event) => {
                              event.dataTransfer.setData('text/plain', player.key);
                              setDragging(player.key);
                            }}
                            onDragEnd={() => setDragging(null)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                              // Soltar em cima de um jogador troca os dois; no
                              // resto do card, só move.
                              event.preventDefault();
                              event.stopPropagation();
                              const key = event.dataTransfer.getData('text/plain');
                              if (key && key !== player.key) swapPlayers(key, player.key);
                              setDragging(null);
                            }}
                            className={cx(
                              'flex cursor-grab items-center gap-2.5 rounded-lg border p-2 transition-colors active:cursor-grabbing',
                              active
                                ? 'border-brand bg-brand-soft'
                                : swapTarget
                                  ? 'border-dashed border-info/50 hover:bg-sunken'
                                  : 'border-transparent hover:bg-sunken',
                              dragging === player.key && 'opacity-40',
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => handlePlayerClick(player.key, teamIndex)}
                              className="flex flex-1 items-center gap-2.5 text-left"
                              aria-pressed={active}
                              aria-label={
                                swapTarget ? `Trocar com ${player.name}` : undefined
                              }
                            >
                              <Avatar
                                name={player.name}
                                size={28}
                                badge={player.isGoalkeeper ? Hand : undefined}
                              />
                              <span className="flex-1 truncate text-[13px]">{player.name}</span>
                              <PositionBadge position={player.position} short />
                              {player.skill !== null ? (
                                <StarRating value={player.skill} size={11} />
                              ) : null}
                            </button>

                            {team.bench ? null : (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleGoalkeeper(player.key);
                              }}
                              aria-label={
                                player.isGoalkeeper
                                  ? `Tirar ${player.name} do gol`
                                  : `Colocar ${player.name} no gol`
                              }
                              title="Definir goleiro"
                              className={cx(
                                'rounded p-1.5 transition-colors',
                                player.isGoalkeeper
                                  ? 'bg-brand text-on-brand'
                                  : 'text-ink-3 hover:bg-sunken hover:text-ink-2',
                              )}
                            >
                              <Hand className="h-3.5 w-3.5" aria-hidden />
                            </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Card>
              ),
            )}
          </div>

          <p className="text-center text-[13px] text-ink-3">
            Toque num jogador e depois num de outro time para trocar os dois. No computador,
            dá também para arrastar.
            <br />O ícone da mão define quem vai ao gol. Depois de ajustar, toque em Salvar.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * Quem chega num time que já tem goleiro marcado perde a marcação — um goleiro
 * por time. O admin remarca pela mão se quiser trocar o goleiro.
 */
function arriving(player: DraftPlayer, destination: DraftTeam): DraftPlayer {
  if (destination.bench) return { ...player, isGoalkeeper: false };
  const hasKeeper = destination.players.some(
    (entry) => entry.key !== player.key && entry.isGoalkeeper,
  );
  return player.isGoalkeeper && hasKeeper ? { ...player, isGoalkeeper: false } : player;
}

/** Goleiro sempre no topo da lista do time — é o primeiro que se procura. */
function keepersFirst(players: DraftPlayer[]): DraftPlayer[] {
  return [...players].sort((a, b) => Number(b.isGoalkeeper) - Number(a.isGoalkeeper));
}
